/**
 * scaler.js — Device-independent proportion scaling engine
 *
 * Reads inspector-values.json and scales all layout + typographic values
 * proportionally to the actual #app container dimensions, so the interface
 * renders with identical proportions on any device or browser.
 *
 * Scale is computed from the most-constrained axis — min(width/refW, height/refH) —
 * ensuring fonts and element heights remain in proportion even when the viewport
 * aspect ratio differs from the reference (e.g., iOS Safari with visible address bar).
 *
 * Writes CSS custom properties to :root for CSS var() consumption.
 */

// ── Constants ──────────────────────────────────────────────────
(() => {
const JSON_URL = './inspector-values.json';

// ── Reference layout values at 430×932 viewport ───────────────
// Derived from the dvh-based percentages in the original CSS.
// All values in px at the reference viewport.
const REF_VIEWPORT = { width: 430, height: 932 };
const REF_LAYOUT = {
  mainTop:        { height: 37.28,  marginTop: 18.64 },          // 4dvh, 2dvh
  dotMatrix:      { height: 372.8,  marginTop: 46.6,  marginBottom: 18.64 }, // 40dvh, 5dvh, 2dvh
  volumeRow:      { height: 93.2 },                               // 10dvh
  navRow:         { height: 111.84, marginTop: 27.96, marginBottom: 27.96 }, // 12dvh, 3dvh
  supportRow:     { height: 46.6 },                               // 5dvh
  dmIndicator:    { height: 139.8 },                              // 15dvh
};

// ── State ──────────────────────────────────────────────────────
let scale = 1;
let refViewport = { width: REF_VIEWPORT.width, height: REF_VIEWPORT.height };
let inspectorValues = null;

// ── Fit station-name font to container width ────────────────────
// Shrinks --dm-fs-name when the text is wider than its container,
// so long station names never wrap to two lines.
const FIT_MIN_FS = 10; // minimum font-size in px after fitting

function fitStationNameToWidth() {
  const zone = document.getElementById('dm-zone-name');
  if (!zone || !inspectorValues) return;

  // Clear any previous inline size override so we measure against the CSS custom property
  zone.style.fontSize = '';

  // If content fits natively, we're done
  if (zone.scrollWidth <= zone.clientWidth) return;

  // Binary search for the largest font-size that fits
  const maxFs = parseFloat(getComputedStyle(zone).fontSize) || (inspectorValues.dmZoneName.fontSize * scale);
  let low = FIT_MIN_FS;
  let high = maxFs;

  while (high - low > 0.25) {
    const mid = (low + high) / 2;
    zone.style.fontSize = mid + 'px';
    if (zone.scrollWidth <= zone.clientWidth) {
      low = mid;
    } else {
      high = mid;
    }
  }
  zone.style.fontSize = low + 'px';
}

// ── MutationObserver: re-fit whenever station-name text changes ─
// Also handles late mount (element not yet in DOM when scaler init runs).
let fitObserver = null;
function watchStationName() {
  const zone = document.getElementById('dm-zone-name');
  if (zone) {
    fitStationNameToWidth();
    if (fitObserver) fitObserver.disconnect();
    fitObserver = new MutationObserver(() => fitStationNameToWidth());
    fitObserver.observe(zone, { characterData: true, childList: true, subtree: true });
  } else {
    // Element doesn't exist yet (Svelte hasn't mounted) — retry on DOMContentLoaded
    document.addEventListener('DOMContentLoaded', () => {
      const el = document.getElementById('dm-zone-name');
      if (el) {
        fitStationNameToWidth();
        fitObserver = new MutationObserver(() => fitStationNameToWidth());
        fitObserver.observe(el, { characterData: true, childList: true, subtree: true });
      }
    }, { once: true });
  }
}

// ── CSS custom property writer ─────────────────────────────────
function setCSSProp(name, value) {
  document.documentElement.style.setProperty(name, value);
}

function writeAllCSSProps() {
  if (!inspectorValues) return;
  const v = inspectorValues;

  setCSSProp('--dm-scale', scale.toFixed(4));

  // dm-zone-meta
  setCSSProp('--dm-fs-meta',  (v.dmZoneMeta.fontSize * scale).toFixed(2) + 'px');
  setCSSProp('--dm-ls-meta',  (v.dmZoneMeta.letterSpacing * scale).toFixed(2) + 'px');

  // dm-zone-meta-wrapper
  setCSSProp('--dm-h-meta-wrapper', (v.dmZoneMetaWrapper.height * scale).toFixed(2) + 'px');

  // dm-zone-name
  setCSSProp('--dm-fs-name',  (v.dmZoneName.fontSize * scale).toFixed(2) + 'px');
  setCSSProp('--dm-ls-name',  (v.dmZoneName.letterSpacing * scale).toFixed(2) + 'px');
  setCSSProp('--dm-h-name',   (v.dmZoneName.height * scale).toFixed(2) + 'px');

  // volume-bar
  setCSSProp('--dm-fs-volbar', (v.volumeBar.fontSize * scale).toFixed(2) + 'px');
  setCSSProp('--dm-h-volbar',  (v.volumeBar.height * scale).toFixed(2) + 'px');

  // volume-bar spans
  setCSSProp('--dm-fs-volspan', (v.volumeBarSpan.fontSize * scale).toFixed(2) + 'px');

  // volume-readout
  setCSSProp('--dm-fs-volreadout', (v.volumeReadout.fontSize * scale).toFixed(2) + 'px');

  // ── Layout heights (deterministic, container-relative) ────
  const L = REF_LAYOUT;
  setCSSProp('--dm-h-main-top',        (L.mainTop.height       * scale).toFixed(2) + 'px');
  setCSSProp('--dm-mt-main-top',       (L.mainTop.marginTop    * scale).toFixed(2) + 'px');
  setCSSProp('--dm-h-dotmatrix',       (L.dotMatrix.height     * scale).toFixed(2) + 'px');
  setCSSProp('--dm-mt-dotmatrix',      (L.dotMatrix.marginTop  * scale).toFixed(2) + 'px');
  setCSSProp('--dm-mb-dotmatrix',      (L.dotMatrix.marginBottom * scale).toFixed(2) + 'px');
  setCSSProp('--dm-h-volume-row',      (L.volumeRow.height     * scale).toFixed(2) + 'px');
  setCSSProp('--dm-h-nav-row',         (L.navRow.height        * scale).toFixed(2) + 'px');
  setCSSProp('--dm-mt-nav-row',        (L.navRow.marginTop     * scale).toFixed(2) + 'px');
  setCSSProp('--dm-mb-nav-row',        (L.navRow.marginBottom  * scale).toFixed(2) + 'px');
  setCSSProp('--dm-h-support-row',     (L.supportRow.height    * scale).toFixed(2) + 'px');
  setCSSProp('--dm-h-dm-indicator',    (L.dmIndicator.height   * scale).toFixed(2) + 'px');

  // Re-fit station name after scale change
  fitStationNameToWidth();
}

// ── Compute scale from the actual #app container ───────────────
// Uses the most-constrained axis (min of width and height scales)
// so that when dvh shrinks on iOS Safari (address bar visible),
// all element sizes scale down proportionally — not just heights.
function computeScale() {
  const app = document.getElementById('app');
  if (app) {
    const rect = app.getBoundingClientRect();
    const scaleW = rect.width  / refViewport.width;
    const scaleH = rect.height / refViewport.height;
    scale = Math.min(scaleW, scaleH);
  } else {
    // Fallback: use viewport dimensions, but clamp to max constraints
    const vw = Math.min(
      document.documentElement.clientWidth || window.innerWidth,
      REF_VIEWPORT.width // max-width of #app
    );
    const vh = Math.min(
      document.documentElement.clientHeight || window.innerHeight,
      REF_VIEWPORT.height // max-height of #app
    );
    const scaleW = vw / refViewport.width;
    const scaleH = vh / refViewport.height;
    scale = Math.min(scaleW, scaleH);
  }

  scale = Math.max(0.5, Math.min(2.0, scale));
}

// ── Resize handler ─────────────────────────────────────────────
const RESIZE_DEBOUNCE_MS = 200;
let resizeTimer = null;
function onResize() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    computeScale();
    writeAllCSSProps();
  }, RESIZE_DEBOUNCE_MS);
}

// ── Init ───────────────────────────────────────────────────────
async function init() {
  try {
    const resp = await fetch(JSON_URL);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();

    refViewport = data.refViewport;
    inspectorValues = data.values;

    // Apply immediately — no waiting for fonts
    computeScale();
    writeAllCSSProps();

    // Watch station-name text changes and re-fit
    watchStationName();

    window.addEventListener('resize', onResize);

    // Re-run after fonts load so any metric shift is reflected (GLM Batch B — T4.1).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        computeScale();
        writeAllCSSProps();
      });
    }
  } catch (err) {
    // keep :root fallback values; scale stays at 1
    scale = 1;
  }
}

// ── Start: run as soon as possible ─────────────────────────────
// Don't wait for fonts — CSS vars are set immediately.
// If #app exists, init now. Otherwise wait for DOM.
if (document.getElementById('app')) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init);
}

})();
